import { Injectable, InjectionToken } from '@angular/core';
import { FormControl, UntypedFormGroup } from '@angular/forms';

import { IFormDocument } from 'app/services/api5-service/api.interface';
import { BehaviorSubject, EMPTY, Observable, combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs/operators';

import { DocHelper } from 'utils/dochelper';
import { filterDefined } from 'utils/is-defined';

import { IFormModel } from '../dynamic-form.interface';
import {
  ISPFieldConfig,
  ISPFieldType,
  ISPFormOptions,
  ISPFormState,
} from '../model';
import { TemplateConfig } from '../types/template/model';
import { getTypedForm, TypedForm } from '../utils/form-preparing';
import { findConfig, forEachConfig } from '../utils/formly-configs';
import { isConfigWithControl } from '../utils/is-config-with-control';

/**
 * Doc service token
 */
export const FORM_DOC = new InjectionToken('doc');

/**
 * Form doc service interface
 *
 * @TODO i.ablov move to a more general place
 */
export interface IFormDocService {
  /** Can be null because of lazy loading */
  readonly doc: IFormDocument | null;
}

export type ValueChangeWithControl = Record<
  string,
  {
    value: IFormModel[string];
    formControl: FormControl;
  }
>;

@Injectable()
export class FormService implements IFormDocService {
  /** Formly options subject */
  private readonly optionsSubject: BehaviorSubject<ISPFormOptions | null> =
    new BehaviorSubject(null);

  /** Form group subject */
  private readonly formGroupSubject: BehaviorSubject<UntypedFormGroup | null> =
    new BehaviorSubject(null);

  /** Form controls subject. In order to get form control */
  private readonly formControlsSubject: BehaviorSubject<
    Record<string, FormControl>
  > = new BehaviorSubject({});

  /** Formly configs subject */
  private readonly configsSubject: BehaviorSubject<ISPFieldConfig[]> =
    new BehaviorSubject([]);

  private readonly modelSubject: BehaviorSubject<IFormModel> =
    new BehaviorSubject({});

  /** Form template configs */
  templateConfigs: TemplateConfig[];

  /** Formly options stream */
  readonly options$ = this.optionsSubject.pipe(filterDefined());

  /** Formly options */
  get options(): ISPFormOptions | null {
    return this.optionsSubject.value;
  }

  /** Form group stream */
  readonly formGroup$ = this.formGroupSubject.pipe(filterDefined());

  /** Form group configs */
  get formGroup(): UntypedFormGroup {
    if (!this.formGroupSubject.value) {
      throw new Error('formGroup is undefined!');
    }
    return this.formGroupSubject.value;
  }

  /** Formly configs stream */
  readonly configs$ = this.configsSubject.asObservable();

  /** Formly configs */
  get configs(): ISPFieldConfig[] {
    return this.configsSubject.value;
  }

  /** Form model stream */
  readonly model$ = this.modelSubject.asObservable();

  /** Form model */
  get model(): IFormModel {
    return this.modelSubject.value;
  }

  /** Formly form state object */
  get formState(): ISPFormState {
    if (!this.optionsSubject.value?.formState) {
      throw new Error('form state is undefined!');
    }
    return this.optionsSubject.value.formState;
  }

  /** Form document stream */
  readonly doc$ = this.options$.pipe(
    switchMap(options => options.formState.docSubject),
  );

  /** Form document */
  get doc(): IFormDocument | null {
    return this.options?.formState.docSubject.value || null;
  }

  /** @WARN Use this doc if you sure that doc is loaded */
  get loadedDoc(): IFormDocument {
    if (!this.doc) {
      throw new Error('doc is not loaded');
    }
    return this.doc;
  }

  /** Form typed form stream. Representation of doc data */
  readonly typedForm$ = this.doc$.pipe(
    map(doc => getTypedForm(doc)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );

  /** Form typed form */
  get typedForm(): TypedForm | undefined {
    return this.doc ? getTypedForm(this.doc) : undefined;
  }

  /** Form model change event */
  readonly valueChanges$: Observable<IFormModel> = this.formGroupSubject.pipe(
    filterDefined(),
    switchMap(form =>
      form.valueChanges.pipe(
        startWith(form.value),
        map(() => form.getRawValue()),
      ),
    ),
  );

  readonly valueChangesOnly$ = this.valueChanges$.pipe(
    pairwise(),
    map(([prev, next]) => {
      const nextEntries = Object.entries(next);

      const modelChanges = nextEntries.reduce<IFormModel>(
        (changes, [nextKey, nextValue]) => {
          if (prev[nextKey] !== nextValue) {
            changes[nextKey] = nextValue;
          }

          return changes;
        },
        {},
      );

      return modelChanges;
    }),
    filter(changes => Object.keys(changes).length > 0),
  );

  readonly valueChangesOnlyWithControl$: Observable<ValueChangeWithControl> =
    combineLatest([this.valueChangesOnly$, this.formGroup$]).pipe(
      map(([changedModel, formGroup]) => {
        return Object.entries(changedModel).reduce<ValueChangeWithControl>(
          (acc, [changesKey, changesValue]) => {
            acc[changesKey] = {
              value: changesValue,
              formControl: formGroup.controls[changesKey] as FormControl,
            };

            return acc;
          },
          {},
        );
      }),
    );

  /**
   * Get value, that considered as 'empty' depending on config type
   *
   * @param config
   */
  private getEmptyValue(config: ISPFieldConfig): string[] | string | undefined {
    if (!isConfigWithControl(config)) {
      return;
    }

    const originalControl = config.props.originalControl;

    const name = originalControl.$name;

    switch (true) {
      case config.type === ISPFieldType.Checkbox:
        return 'off';
      case config.type === ISPFieldType.Select && config.props.multiple:
        return [];
      case config.type === ISPFieldType.Select && !config.props.multiple: {
        const firstAvailableOption =
          this.formState.selectService.getOptionsForSelect(name)?.[0]?.$key;
        // @WARN this is not... empty value. Ideally we should return empty string. Don't know where this logic can broke something... in mixed controls mechanism?
        // @TODO connected to autoPickSelectOptions mechanism, we should unite this logic somehow
        return firstAvailableOption || 'null';
      }
      default:
        return '';
    }
  }

  /**
   * Reduce the existing form model to contain only "empty" (in a point of BE view) values. Needed to properly reset the form.
   */
  private getEmptyModel(): IFormModel {
    const model = {};

    forEachConfig(this.configs, config => {
      if (!isConfigWithControl(config)) {
        return;
      }

      const originalControl = config.props.originalControl;
      const name = originalControl.$name;
      const emptyValue = this.getEmptyValue(config);

      if (emptyValue !== undefined) {
        model[name] = emptyValue;
      }
    });

    return model;
  }

  /**
   * Value changes of specific formControl
   *
   * @WARN use this function, instead of FormGroup.controls[name].valueChanges, because controls are setted in
   * runtime by formly, and you can get no formControls
   * @param formControlName - form control name
   * @param emitFirst - emit first value of control
   */
  formControlValueChange$(
    formControlName: string,
    emitFirst?: boolean,
  ): Observable<IFormModel[keyof IFormModel]> {
    return this.formControlsSubject.pipe(
      map(controls => controls[formControlName]),
      switchMap(control => {
        if (!control) {
          return EMPTY;
        }

        return emitFirst
          ? control.valueChanges.pipe(startWith(control.value))
          : control.valueChanges;
      }),
      distinctUntilChanged(),
    );
  }

  formControl$(formControlName: string): Observable<FormControl> {
    return this.formControlsSubject.pipe(
      map(controls => controls[formControlName]),
      distinctUntilChanged(),
      filter(control => Boolean(control)),
    );
  }

  formControl(formControlName: string): FormControl | undefined {
    return this.formControlsSubject.value[formControlName];
  }

  setControlValue(formControlName: string, value: IFormModel[string]): void {
    this.formControlsSubject.value[formControlName]?.setValue(value);
  }

  createFormControl<T = IFormModel[string]>(
    name: string,
    initialValue: T,
    checkExisted = true,
  ): FormControl<T> {
    if (checkExisted && this.formGroup.controls[name]) {
      return this.formGroup.controls[name] as FormControl<T>;
    }

    const formControl = new FormControl<T>(initialValue, {
      nonNullable: true,
    });
    this.formGroup.setControl(name, formControl);
    this.formControlsSubject.next({
      ...this.formControlsSubject.value,
      [name]: formControl,
    });

    return formControl;
  }

  setState(state: ISPFormState): void {
    this.optionsSubject.next({
      ...this.optionsSubject.value,
      formState: {
        ...this.optionsSubject.value?.formState,
        ...state,
      },
    });
  }

  setFormGroup(formGroup: UntypedFormGroup): void {
    this.formGroupSubject.next(formGroup);
    this.formControlsSubject.next({ ...formGroup.controls } as Record<
      string,
      FormControl
    >);
  }

  /**
   * Form need to be notified after some form control will be added by formly. So we use this function to notify state
   * on form control adding
   *
   * @param name
   * @param formControl
   */
  registerFormControl(name: string, formControl: FormControl): void {
    this.formControlsSubject.next({
      ...this.formControlsSubject.value,
      [name]: formControl,
    });
  }

  /**
   * Form need to be notified after some form control will be removed by formly. So we use this function to notify state
   * on form control remove
   *
   * @param name
   */
  unregisterFormControl(name: string): void {
    if (!this.formControlsSubject.value[name]) {
      return;
    }

    delete this.formControlsSubject.value[name];
    this.formControlsSubject.next({
      ...this.formControlsSubject.value,
    });
  }

  /**
   * Append lifecycle hooks to detect fields form controls
   *
   * @param config
   */
  appendHooks(config: ISPFieldConfig): void {
    if (!isConfigWithControl(config)) {
      return;
    }

    const originalControl = config.props.originalControl;
    config.hooks = {
      ...config.hooks,
      onInit: (c: ISPFieldConfig) => {
        const formControl = c.formControl as FormControl;
        this.registerFormControl(originalControl.$name, formControl);
      },
      onDestroy: () => {
        this.unregisterFormControl(originalControl.$name);
      },
    };
  }

  setConfigs(configs: ISPFieldConfig[]): void {
    forEachConfig(configs, config => this.appendHooks(config));

    this.configsSubject.next(configs);
  }

  setModel(model: IFormModel): void {
    this.modelSubject.next(model);
  }

  setDoc(doc: IFormDocument): void {
    this.options?.formState.docSubject.next(doc);
  }

  syncModelWithDoc(): void {
    const docModel = DocHelper.getFormModel(this.doc);

    Object.keys(this.model).forEach(key => {
      delete this.model[key];
    });

    Object.assign(this.model, docModel);
    this.modelSubject.next(this.modelSubject.value);
  }

  /**
   * Clear form group model
   */
  clearForm(): void {
    // @TODO i.ablov update currenct modelSubject?
    const emptyModel = this.getEmptyModel();
    this.formGroup.reset(emptyModel, { onlySelf: true });
  }

  /**
   * Clear form field control
   *
   * @param formControlName - control name
   */
  clearControl(formControlName: string): void {
    const config = findConfig(this.configs, c => c.key === formControlName);
    const formControl = config?.formControl;

    if (formControl && config) {
      const emptyValue = this.getEmptyValue(config);

      if (emptyValue !== undefined) {
        formControl.reset(emptyValue);
      }
    }
  }

  /**
   * Reset form model
   */
  resetForm(): void {
    this.syncModelWithDoc();
    this.formGroup.reset(this.model, { onlySelf: true });
  }
}
